import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonIcon from '@mui/icons-material/Person';
import SaveIcon from '@mui/icons-material/Save';
import StarIcon from '@mui/icons-material/Star';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, CircularProgress, Grid, IconButton, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';

import { Context } from '../../service/Context';
import theme from '../../styles/theme';
import { capitalizar } from '../../utils/UtilesComun';
import { obtenerCategoriaCodigo, obtenerColorNota, obtenerEstadoJugadorEnCategoria } from '../../utils/UtilesJugador';
import { AlertApp, BadgeInfoApp, ButtonApp, DialogApp, TextFieldApp, TypographyApp } from '../custom/CustomComponents';

/**
 * Encabezado de sección para Titulares y Suplentes.
 */
const CabeceraSeccion = ({ titulo, total }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      py: 0.5,
      px: 1,
      mt: 1,
      mb: 0.5,
      backgroundColor: theme.palette.custom.tablas,
      borderRadius: '4px',
      borderLeft: `3px solid ${theme.palette.primary.main}`,
    }}
  >
    <PersonIcon sx={{ fontSize: '0.95rem', color: theme.palette.primary.main }} />
    <TypographyApp sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {titulo} ({total})
    </TypographyApp>
  </Box>
);

CabeceraSeccion.propTypes = {
  titulo: PropTypes.string.isRequired,
  total: PropTypes.number.isRequired,
};

/**
 * Fila ultra compacta para evaluar un jugador en el modal.
 */
const FilaEvaluacionCompacta = ({
  jugador,
  calificacion,
  observacion,
  opcionesCalificacion,
  onCalificacionChange,
  onObservacionChange,
  anoTemporada,
  categoriaEquipo,
}) => {
  const estiloNota = obtenerColorNota(calificacion);
  const tieneNota = calificacion !== '' && calificacion !== null && calificacion !== undefined;

  const renderIdentificador = () => {
    if (jugador.dorsal !== null && jugador.dorsal !== undefined && jugador.dorsal !== '') {
      return (
        <BadgeInfoApp sx={{ mr: 0, minWidth: 24, fontSize: '0.7rem', py: 0.1, px: 0.5 }}>{jugador.dorsal}</BadgeInfoApp>
      );
    }

    const codigoCat = jugador.ano && anoTemporada ? obtenerCategoriaCodigo(anoTemporada, jugador.ano) : null;
    if (codigoCat) {
      const estadoCat = categoriaEquipo ? obtenerEstadoJugadorEnCategoria(codigoCat, categoriaEquipo.toUpperCase()) : 0;
      let bg = '#f1f5f9';
      let textColor = '#475569';
      let borderColor = '#cbd5e1';
      let tooltipText = codigoCat;

      if (estadoCat >= 1) {
        bg = '#e2f7f3';
        textColor = '#0f766e';
        borderColor = '#99f6e4';
        tooltipText = `${codigoCat} (Plantilla)`;
      } else if (estadoCat < 0) {
        bg = '#ffe4e6';
        textColor = '#be123c';
        borderColor = '#fecdd3';
        tooltipText = `${codigoCat} (Cat. inferior)`;
      }

      return (
        <Tooltip title={tooltipText} arrow placement="top">
          <Box
            sx={{
              minWidth: 24,
              height: 20,
              px: 0.4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: bg,
              color: textColor,
              border: `1px solid ${borderColor}`,
              borderRadius: '4px',
              fontSize: '0.68rem',
              fontWeight: 700,
              letterSpacing: '-0.02em',
              userSelect: 'none',
              cursor: 'default',
            }}
          >
            {codigoCat}
          </Box>
        </Tooltip>
      );
    }

    return <BadgeInfoApp sx={{ mr: 0, minWidth: 24, fontSize: '0.7rem', py: 0.1, px: 0.5 }}>-</BadgeInfoApp>;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        py: 0.35,
        px: 1,
        borderRadius: '4px',
        backgroundColor: tieneNota ? `${estiloNota.bg}50` : 'transparent',
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.03)',
        },
      }}
    >
      <Grid container spacing={1} alignItems="center">
        {/* Dorsal o Categoría y Nombre del Jugador */}
        <Grid item xs={12} sm={3.6} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, overflow: 'hidden' }}>
          {renderIdentificador()}

          <TypographyApp sx={{ fontWeight: 600, fontSize: '0.82rem', whiteSpace: 'nowrap' }} handleOverflow>
            {capitalizar(jugador.nombreJugador)}
          </TypographyApp>
        </Grid>

        {/* Selector de Calificación con Paleta Visual Verde a Rojo */}
        <Grid item xs={3.5} sm={1.4}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Select
              size="small"
              value={calificacion ?? ''}
              onChange={(e) => onCalificacionChange(jugador.idJugador, e.target.value)}
              displayEmpty
              sx={{
                height: 26,
                minWidth: 68,
                maxWidth: 72,
                width: '100%',
                fontSize: '0.8rem',
                fontWeight: tieneNota ? 700 : 400,
                backgroundColor: estiloNota.bg,
                color: estiloNota.color,
                border: `1px solid ${estiloNota.border}`,
                borderRadius: '4px',
                '& .MuiSelect-select': {
                  py: 0.25,
                  px: 0.75,
                  color: estiloNota.color,
                },
                '& .MuiSelect-icon': {
                  color: estiloNota.color,
                },
                '& fieldset': {
                  border: 'none',
                },
              }}
            >
              <MenuItem value="" sx={{ fontSize: '0.8rem' }}>
                <em>-</em>
              </MenuItem>
              {opcionesCalificacion.map((opt) => {
                const optColor = obtenerColorNota(opt);
                return (
                  <MenuItem
                    key={opt}
                    value={opt}
                    sx={{
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      backgroundColor: `${optColor.bg}88`,
                      color: optColor.color,
                      '&:hover': {
                        backgroundColor: optColor.bg,
                      },
                    }}
                  >
                    {opt}
                  </MenuItem>
                );
              })}
            </Select>
          </Box>
        </Grid>

        {/* Campo de Observación Rápida Compacto */}
        <Grid item xs={8.5} sm={7}>
          <TextFieldApp
            size="small"
            placeholder="Observación cualitativa..."
            value={observacion ?? ''}
            onChange={(e) => onObservacionChange(jugador.idJugador, e.target.value)}
            title={observacion || undefined}
            sx={{
              width: '100%',
              margin: 0,
              padding: 0,
              '& .MuiInputBase-root': {
                height: 26,
                fontSize: '0.78rem',
                py: 0,
                px: 0.75,
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
              },
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

FilaEvaluacionCompacta.propTypes = {
  jugador: PropTypes.object.isRequired,
  calificacion: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  observacion: PropTypes.string,
  opcionesCalificacion: PropTypes.array.isRequired,
  onCalificacionChange: PropTypes.func.isRequired,
  onObservacionChange: PropTypes.func.isRequired,
  anoTemporada: PropTypes.number,
  categoriaEquipo: PropTypes.string,
};

/**
 * Modal para evaluar a los jugadores de un equipo en un partido determinado.
 */
const ModalValorarPartido = ({ open, onClose, partido, idEquipo, categoriaEquipo, temporada, onGuardadoExitoso }) => {
  const {
    obtenerEvaluacionesPartido,
    guardarEvaluacionesPartido,
    eliminarEvaluacionesPartido,
    partidosCache,
    setPartidoCacheItem,
  } = useContext(Context);

  const [jugadores, setJugadores] = useState([]);
  const [evaluaciones, setEvaluaciones] = useState({});
  const [observaciones, setObservaciones] = useState({});
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [borrando, setBorrando] = useState(false);
  const [confirmacionBorradoOpen, setConfirmacionBorradoOpen] = useState(false);
  const [error, setError] = useState(null);

  // Cargar jugadores y evaluaciones existentes al abrir el modal
  useEffect(() => {
    let isMounted = true;

    const cargarDatosPartido = async () => {
      if (!open || !partido || !idEquipo) {
        return;
      }

      const idPartido = partido.idPartido;
      const cachedData = partidosCache?.[idPartido]?.evaluacionesEquipo?.[idEquipo];

      if (cachedData && Array.isArray(cachedData) && cachedData.length > 0) {
        setJugadores(cachedData);
        const evalsIniciales = {};
        const obsIniciales = {};
        cachedData.forEach((j) => {
          if (j.calificacion !== null && j.calificacion !== undefined) {
            evalsIniciales[j.idJugador] = j.calificacion;
          }
          if (j.textoObservacion) {
            obsIniciales[j.idJugador] = j.textoObservacion;
          }
        });
        setEvaluaciones(evalsIniciales);
        setObservaciones(obsIniciales);
        setLoading(false);
      } else {
        setJugadores([]);
        setEvaluaciones({});
        setObservaciones({});
        setLoading(true);
      }
      setError(null);

      try {
        const datos = await obtenerEvaluacionesPartido(idPartido, idEquipo);
        if (isMounted) {
          const list = datos || [];
          setJugadores(list);
          const evalsIniciales = {};
          const obsIniciales = {};
          list.forEach((j) => {
            if (j.calificacion !== null && j.calificacion !== undefined) {
              evalsIniciales[j.idJugador] = j.calificacion;
            }
            if (j.textoObservacion) {
              obsIniciales[j.idJugador] = j.textoObservacion;
            }
          });
          setEvaluaciones(evalsIniciales);
          setObservaciones(obsIniciales);

          if (setPartidoCacheItem) {
            const currentEvalEquipos = partidosCache?.[idPartido]?.evaluacionesEquipo || {};
            setPartidoCacheItem(idPartido, {
              evaluacionesEquipo: {
                ...currentEvalEquipos,
                [idEquipo]: list,
              },
            });
          }
        }
      } catch (err) {
        if (isMounted) {
          console.error('Error al cargar datos de evaluación:', err);
          if (!cachedData) {
            setError('No se pudieron cargar los jugadores para este partido.');
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    cargarDatosPartido();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, partido, idEquipo]);

  const handleCalificacionChange = (idJugador, val) => {
    setEvaluaciones((prev) => ({
      ...prev,
      [idJugador]: val === '' ? '' : parseFloat(val),
    }));
  };

  const handleObservacionChange = (idJugador, texto) => {
    setObservaciones((prev) => ({
      ...prev,
      [idJugador]: texto,
    }));
  };

  const handleGuardar = async () => {
    setGuardando(true);
    setError(null);

    const idsAfectados = new Set([...Object.keys(evaluaciones), ...Object.keys(observaciones)]);
    const payload = Array.from(idsAfectados)
      .filter((id) => {
        const tieneCalif = evaluaciones[id] !== '' && evaluaciones[id] !== null && evaluaciones[id] !== undefined;
        const tieneObs = observaciones[id] && observaciones[id].trim() !== '';
        return tieneCalif || tieneObs;
      })
      .map((id) => ({
        idJugador: parseInt(id, 10),
        calificacion:
          evaluaciones[id] !== '' && evaluaciones[id] !== null && evaluaciones[id] !== undefined
            ? Number(evaluaciones[id])
            : null,
        textoObservacion: observaciones[id] ? observaciones[id].trim() : '',
      }));

    if (payload.length === 0) {
      setError('Introduce al menos una calificación u observación antes de guardar.');
      setGuardando(false);
      return;
    }

    try {
      const ok = await guardarEvaluacionesPartido(partido.idPartido, payload);
      if (ok) {
        if (setPartidoCacheItem) {
          const currentEvalEquipos = partidosCache?.[partido.idPartido]?.evaluacionesEquipo || {};
          const nextEvalEquipos = { ...currentEvalEquipos };
          delete nextEvalEquipos[idEquipo];
          setPartidoCacheItem(partido.idPartido, { evaluacionesEquipo: nextEvalEquipos });
        }
        if (onGuardadoExitoso) {
          onGuardadoExitoso();
        }
        onClose();
      } else {
        setError('Ocurrió un error al guardar las evaluaciones.');
      }
    } catch (err) {
      console.error('Error al guardar evaluaciones:', err);
      setError('Ocurrió un error inesperado al guardar.');
    } finally {
      setGuardando(false);
    }
  };

  const handleConfirmarBorrado = async () => {
    setBorrando(true);
    setError(null);

    try {
      const ok = await eliminarEvaluacionesPartido(partido.idPartido);
      if (ok) {
        if (setPartidoCacheItem) {
          setPartidoCacheItem(partido.idPartido, { evaluacionesEquipo: {} });
        }
        setEvaluaciones({});
        setObservaciones({});
        setConfirmacionBorradoOpen(false);
        if (onGuardadoExitoso) {
          onGuardadoExitoso();
        }
        onClose();
      } else {
        setError('Ocurrió un error al borrar las evaluaciones.');
        setConfirmacionBorradoOpen(false);
      }
    } catch (err) {
      console.error('Error al borrar evaluaciones:', err);
      setError('Ocurrió un error inesperado al borrar las evaluaciones.');
      setConfirmacionBorradoOpen(false);
    } finally {
      setBorrando(false);
    }
  };

  const anoTemporada =
    temporada && temporada.includes('-')
      ? parseInt(temporada.split('-')[1], 10)
      : partido?.fecha
        ? new Date(partido.fecha).getFullYear()
        : new Date().getFullYear();

  const fechaFormateada = partido?.fecha
    ? new Date(partido.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '';

  const tituloModal = partido
    ? `Evaluar Partido: J${partido.jornada || '-'} (${fechaFormateada}) - ${partido.rival || 'Rival'}`
    : 'Evaluar Partido';

  // Opciones de calificación del 1 al 10 con saltos de 0.5
  const opcionesCalificacion = [];
  for (let i = 10; i >= 1; i -= 0.5) {
    opcionesCalificacion.push(Number(i.toFixed(1)));
  }

  // Separar en Titulares, Suplentes y No Convocados
  const ordenarPorDorsal = (a, b) => {
    const dorsalA = a.dorsal !== null && a.dorsal !== undefined ? Number(a.dorsal) : 999;
    const dorsalB = b.dorsal !== null && b.dorsal !== undefined ? Number(b.dorsal) : 999;
    if (dorsalA !== dorsalB) {
      return dorsalA - dorsalB;
    }
    return (a.nombreJugador || '').localeCompare(b.nombreJugador || '');
  };

  const ordenarNoConvocados = (a, b) => {
    const codA = a.ano && anoTemporada ? obtenerCategoriaCodigo(anoTemporada, a.ano) : null;
    const codB = b.ano && anoTemporada ? obtenerCategoriaCodigo(anoTemporada, b.ano) : null;
    const estA = codA && categoriaEquipo ? obtenerEstadoJugadorEnCategoria(codA, categoriaEquipo.toUpperCase()) : -99;
    const estB = codB && categoriaEquipo ? obtenerEstadoJugadorEnCategoria(codB, categoriaEquipo.toUpperCase()) : -99;

    if (estA !== estB) {
      return estB - estA;
    }
    if (codA !== codB) {
      if (!codA) {
        return 1;
      }
      if (!codB) {
        return -1;
      }
      const cmpCod = codB.localeCompare(codA);
      if (cmpCod !== 0) {
        return cmpCod;
      }
    }
    return (a.nombreJugador || '').localeCompare(b.nombreJugador || '');
  };

  const titulares = jugadores.filter((j) => j.convocado !== false && j.titular === true).sort(ordenarPorDorsal);
  const suplentes = jugadores.filter((j) => j.convocado !== false && j.titular === false).sort(ordenarPorDorsal);
  const noConvocados = jugadores.filter((j) => j.convocado === false).sort(ordenarNoConvocados);
  const sinDefinir = jugadores
    .filter((j) => j.convocado === null && (j.titular === null || j.titular === undefined))
    .sort(ordenarPorDorsal);

  return (
    <>
      <DialogApp
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            maxWidth: { xs: '96vw', sm: 900, md: 960 },
            width: '100%',
          },
        }}
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StarIcon sx={{ color: theme.palette.primary.main, fontSize: '1.3rem' }} />
              <TypographyApp sx={{ fontWeight: 700, fontSize: '1.05rem' }}>{tituloModal}</TypographyApp>
            </Box>
            <IconButton onClick={onClose} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        }
        actions={
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', p: 0.5 }}>
            <ButtonApp
              onClick={() => setConfirmacionBorradoOpen(true)}
              disabled={guardando || loading || borrando}
              sx={{
                color: theme.palette.fail.main,
                borderColor: theme.palette.fail.light,
                '&:hover': {
                  backgroundColor: theme.palette.fail.light,
                },
              }}
              startIcon={<DeleteOutlineIcon fontSize="small" />}
            >
              Limpiar
            </ButtonApp>

            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <ButtonApp onClick={onClose} disabled={guardando || borrando}>
                Cancelar
              </ButtonApp>
              <ButtonApp
                contrast
                onClick={handleGuardar}
                disabled={guardando || loading || borrando}
                startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              >
                {guardando ? 'Guardando...' : 'Guardar'}
              </ButtonApp>
            </Box>
          </Box>
        }
      >
        {error && (
          <AlertApp severity="error" sx={{ mb: 1.5 }}>
            {error}
          </AlertApp>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress size={32} />
          </Box>
        ) : jugadores.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <TypographyApp sx={{ color: 'text.secondary' }}>
              No se encontraron jugadores convocados o registrados para este partido.
            </TypographyApp>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: '72vh', overflowY: 'auto', pr: 0.5 }}>
            {/* Bloque Titulares */}
            {titulares.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <CabeceraSeccion titulo="Titulares" total={titulares.length} />
                {titulares.map((jugador) => (
                  <FilaEvaluacionCompacta
                    key={jugador.idJugador}
                    jugador={jugador}
                    calificacion={evaluaciones[jugador.idJugador]}
                    observacion={observaciones[jugador.idJugador]}
                    opcionesCalificacion={opcionesCalificacion}
                    onCalificacionChange={handleCalificacionChange}
                    onObservacionChange={handleObservacionChange}
                    anoTemporada={anoTemporada}
                    categoriaEquipo={categoriaEquipo}
                  />
                ))}
              </Box>
            )}

            {/* Bloque Suplentes */}
            {suplentes.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <CabeceraSeccion titulo="Suplentes" total={suplentes.length} />
                {suplentes.map((jugador) => (
                  <FilaEvaluacionCompacta
                    key={jugador.idJugador}
                    jugador={jugador}
                    calificacion={evaluaciones[jugador.idJugador]}
                    observacion={observaciones[jugador.idJugador]}
                    opcionesCalificacion={opcionesCalificacion}
                    onCalificacionChange={handleCalificacionChange}
                    onObservacionChange={handleObservacionChange}
                    anoTemporada={anoTemporada}
                    categoriaEquipo={categoriaEquipo}
                  />
                ))}
              </Box>
            )}

            {/* Bloque No Convocados */}
            {noConvocados.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <CabeceraSeccion titulo="No Convocados" total={noConvocados.length} />
                {noConvocados.map((jugador) => (
                  <FilaEvaluacionCompacta
                    key={jugador.idJugador}
                    jugador={jugador}
                    calificacion={evaluaciones[jugador.idJugador]}
                    observacion={observaciones[jugador.idJugador]}
                    opcionesCalificacion={opcionesCalificacion}
                    onCalificacionChange={handleCalificacionChange}
                    onObservacionChange={handleObservacionChange}
                    anoTemporada={anoTemporada}
                    categoriaEquipo={categoriaEquipo}
                  />
                ))}
              </Box>
            )}

            {/* Bloque Convocados (si no tienen indicador titular/suplente) */}
            {sinDefinir.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <CabeceraSeccion titulo="Plantilla Convocada" total={sinDefinir.length} />
                {sinDefinir.map((jugador) => (
                  <FilaEvaluacionCompacta
                    key={jugador.idJugador}
                    jugador={jugador}
                    calificacion={evaluaciones[jugador.idJugador]}
                    observacion={observaciones[jugador.idJugador]}
                    opcionesCalificacion={opcionesCalificacion}
                    onCalificacionChange={handleCalificacionChange}
                    onObservacionChange={handleObservacionChange}
                    anoTemporada={anoTemporada}
                    categoriaEquipo={categoriaEquipo}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogApp>

      {/* Diálogo de Confirmación de Seguridad para "Borrar Todo" */}
      <DialogApp
        open={confirmacionBorradoOpen}
        onClose={() => setConfirmacionBorradoOpen(false)}
        maxWidth="xs"
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningAmberIcon sx={{ color: theme.palette.fail.main, fontSize: '1.4rem' }} />
            <TypographyApp sx={{ fontWeight: 700, fontSize: '1rem' }}>¿Borrar todas las evaluaciones?</TypographyApp>
          </Box>
        }
        actions={
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', gap: 1 }}>
            <ButtonApp onClick={() => setConfirmacionBorradoOpen(false)} disabled={borrando}>
              Cancelar
            </ButtonApp>
            <ButtonApp
              onClick={handleConfirmarBorrado}
              disabled={borrando}
              sx={{
                backgroundColor: theme.palette.fail.main,
                color: '#ffffff',
                '&:hover': {
                  backgroundColor: '#b91c1c',
                },
              }}
              startIcon={borrando ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
            >
              {borrando ? 'Borrando...' : 'Sí, borrar todo'}
            </ButtonApp>
          </Box>
        }
      >
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
          ¿Estás seguro de que deseas eliminar todas las calificaciones y observaciones registradas para este partido?
          Esta acción es irreversible.
        </Typography>
      </DialogApp>
    </>
  );
};

ModalValorarPartido.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  partido: PropTypes.object,
  idEquipo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  categoriaEquipo: PropTypes.string,
  temporada: PropTypes.string,
  onGuardadoExitoso: PropTypes.func,
};

export default ModalValorarPartido;
